/**
 * Token Manager Utility
 * Handles secure storage and validation of JWT tokens with encryption
 */

const ACCESS_TOKEN_KEY = 'meal_mate_access_token'
const REFRESH_TOKEN_KEY = 'meal_mate_refresh_token'
const USER_DATA_KEY = 'meal_mate_user_data'
const ENCRYPTION_KEY_NAME = 'meal_mate_enc_key'

export interface TokenManager {
  getAccessToken(): Promise<string | null>
  getRefreshToken(): Promise<string | null>
  getUserData(): Promise<any | null>
  setTokens(accessToken: string, refreshToken: string, userData?: any): Promise<void>
  setUserData(userData: any): Promise<void>
  clearTokens(): void
  isTokenExpired(token: string): boolean
}

/**
 * Encryption utilities using Web Crypto API with environment-based keys
 */
class CryptoManager {
  private static instance: CryptoManager
  private encryptionKey: CryptoKey | null = null

  static getInstance(): CryptoManager {
    if (!CryptoManager.instance) {
      CryptoManager.instance = new CryptoManager()
    }
    return CryptoManager.instance
  }

  private async getEncryptionKey(): Promise<CryptoKey> {
    if (this.encryptionKey) {
      return this.encryptionKey
    }

    // Get encryption key from environment variables
    const envKey = import.meta.env.VITE_JWT_ENCRYPTION_KEY
    if (!envKey) {
      throw new Error('VITE_JWT_ENCRYPTION_KEY environment variable is required for secure token storage')
    }

    // Validate key format (should be 64 hex characters for 256-bit key)
    if (!/^[0-9a-fA-F]{64}$/.test(envKey)) {
      throw new Error('VITE_JWT_ENCRYPTION_KEY must be a 64-character hexadecimal string (256-bit key)')
    }

    try {
      // Convert hex string to Uint8Array
      const keyData = new Uint8Array(
        envKey.match(/.{1,2}/g)!.map((byte: string) => parseInt(byte, 16))
      )

      // Import the key for AES-GCM encryption
      this.encryptionKey = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'AES-GCM' },
        false,
        ['encrypt', 'decrypt']
      )

      return this.encryptionKey
    } catch (error) {
      console.error('Failed to import encryption key:', error)
      throw new Error('Invalid encryption key format')
    }
  }

  async encrypt(data: string): Promise<string> {
    try {
      const key = await this.getEncryptionKey()
      const iv = crypto.getRandomValues(new Uint8Array(12)) // 96-bit IV for AES-GCM
      const encodedData = new TextEncoder().encode(data)

      const encryptedData = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        encodedData
      )

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encryptedData.byteLength)
      combined.set(iv)
      combined.set(new Uint8Array(encryptedData), iv.length)

      // Convert to base64 for storage
      return btoa(String.fromCharCode(...combined))
    } catch (error) {
      console.error('Encryption failed:', error)
      throw new Error('Failed to encrypt data')
    }
  }

  async decrypt(encryptedData: string): Promise<string> {
    try {
      const key = await this.getEncryptionKey()

      // Convert from base64
      const combined = new Uint8Array(
        atob(encryptedData).split('').map(char => char.charCodeAt(0))
      )

      // Extract IV and encrypted data
      const iv = combined.slice(0, 12)
      const data = combined.slice(12)

      const decryptedData = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        data
      )

      return new TextDecoder().decode(decryptedData)
    } catch (error) {
      console.error('Decryption failed:', error)
      throw new Error('Failed to decrypt data')
    }
  }

  clearEncryptionKey(): void {
    this.encryptionKey = null
    localStorage.removeItem(ENCRYPTION_KEY_NAME)
  }
}

/**
 * Decodes JWT token payload without verification
 * Used for checking expiration time
 */
function decodeJWTPayload(token: string): any {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Error decoding JWT token:', error)
    return null
  }
}

/**
 * Token Manager Implementation
 * Uses localStorage with encryption for secure token persistence across browser sessions
 * In production, consider using httpOnly cookies for refresh tokens
 */
class TokenManagerImpl implements TokenManager {
  private cryptoManager = CryptoManager.getInstance()

  async getAccessToken(): Promise<string | null> {
    try {
      const encryptedToken = localStorage.getItem(ACCESS_TOKEN_KEY)
      if (!encryptedToken) {
        return null
      }
      
      return await this.cryptoManager.decrypt(encryptedToken)
    } catch (error) {
      console.error('Error retrieving access token:', error)
      // Clear corrupted token
      localStorage.removeItem(ACCESS_TOKEN_KEY)
      return null
    }
  }

  async getRefreshToken(): Promise<string | null> {
    try {
      const encryptedToken = localStorage.getItem(REFRESH_TOKEN_KEY)
      if (!encryptedToken) return null

      return await this.cryptoManager.decrypt(encryptedToken)
    } catch (error) {
      console.error('Error retrieving refresh token:', error)
      // Clear corrupted token
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      return null
    }
  }

  async getUserData(): Promise<any | null> {
    try {
      const encryptedUserData = localStorage.getItem(USER_DATA_KEY)
      if (!encryptedUserData) {
        return null
      }
      
      const decryptedUserData = await this.cryptoManager.decrypt(encryptedUserData)
      return JSON.parse(decryptedUserData)
    } catch (error) {
      console.error('Error retrieving user data:', error)
      // Clear corrupted user data
      localStorage.removeItem(USER_DATA_KEY)
      return null
    }
  }

  async setTokens(accessToken: string, refreshToken: string, userData?: any): Promise<void> {
    try {
      const encryptedAccessToken = await this.cryptoManager.encrypt(accessToken)
      const encryptedRefreshToken = await this.cryptoManager.encrypt(refreshToken)

      localStorage.setItem(ACCESS_TOKEN_KEY, encryptedAccessToken)
      localStorage.setItem(REFRESH_TOKEN_KEY, encryptedRefreshToken)

      if (userData) {
        const encryptedUserData = await this.cryptoManager.encrypt(JSON.stringify(userData))
        localStorage.setItem(USER_DATA_KEY, encryptedUserData)
      }
    } catch (error) {
      console.error('Error storing tokens and user data:', error)
      throw new Error('Failed to store authentication tokens and user data')
    }
  }

  async setUserData(userData: any): Promise<void> {
    try {
      const encryptedUserData = await this.cryptoManager.encrypt(JSON.stringify(userData))
      localStorage.setItem(USER_DATA_KEY, encryptedUserData)
    } catch (error) {
      console.error('Error storing user data:', error)
      throw new Error('Failed to store user data')
    }
  }

  clearTokens(): void {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      localStorage.removeItem(USER_DATA_KEY)
      // Also clear the encryption key for complete logout
      this.cryptoManager.clearEncryptionKey()
    } catch (error) {
      console.error('Error clearing tokens:', error)
    }
  }

  isTokenExpired(token: string): boolean {
    if (!token) return true

    const payload = decodeJWTPayload(token)
    if (!payload || !payload.exp) return true

    // Check if token expires within the next 30 seconds (buffer for network latency)
    const expirationTime = payload.exp * 1000 // Convert to milliseconds
    const currentTime = Date.now()
    const bufferTime = 30 * 1000 // 30 seconds buffer

    return currentTime >= (expirationTime - bufferTime)
  }

  // Synchronous methods for backward compatibility (fallback to null if encryption fails)
  getAccessTokenSync(): string | null {
    try {
      const encryptedToken = localStorage.getItem(ACCESS_TOKEN_KEY)
      if (!encryptedToken) return null

      // For synchronous access, we can't decrypt, so return null
      // This encourages using the async methods
      console.warn('Use getAccessToken() async method for secure token access')
      return null
    } catch (error) {
      console.error('Error in sync token access:', error)
      return null
    }
  }

  getRefreshTokenSync(): string | null {
    try {
      const encryptedToken = localStorage.getItem(REFRESH_TOKEN_KEY)
      if (!encryptedToken) return null

      // For synchronous access, we can't decrypt, so return null
      // This encourages using the async methods
      console.warn('Use getRefreshToken() async method for secure token access')
      return null
    } catch (error) {
      console.error('Error in sync token access:', error)
      return null
    }
  }
}

// Export singleton instance
export const tokenManager = new TokenManagerImpl()